<template>
  <div class="transaction-report">
    <!-- 时间筛选器 -->
    <div class="time-filter-section">
      <div class="filter-header">
        <h4 class="filter-title">时间筛选</h4>
        
      </div>
      <div class="filter-content">
        <a-range-picker v-model="localDateRange" format="YYYY-MM-DD" :ranges="dateRanges"
          @change="handleDateRangeChange" style="width: 300px;" placeholder="['开始日期', '结束日期']" />
      </div>
      <div class="current-filter" v-if="localStartTime && localEndTime">
        <a-tag color="blue">
          <a-icon type="calendar" />
          当前筛选: {{ formatDateRange(localStartTime, localEndTime) }}
        </a-tag>
      </div>

      <a-button type="link" size="small" @click="handleResetTimeFilter" :disabled="!localStartTime && !localEndTime">
          <a-icon type="reload" />
          显示全部
        </a-button>
    </div>

    <!-- 统计卡片 -->
    <div class="summary-cards">
      <div class="summary-card income">
        <div class="label">总收入</div>
        <div class="value">{{ totalIncome.toLocaleString('en-US', {
          minimumFractionDigits: 2, maximumFractionDigits: 2
          }) }}</div>
      </div>
      <div class="summary-card expense">
        <div class="label">总支出</div>
        <div class="value">{{ totalExpense.toLocaleString('en-US', {
          minimumFractionDigits: 2, maximumFractionDigits: 2
          }) }}</div>
      </div>
      <div class="summary-card balance" :class="{ 'negative': totalBalance < 0 }">
        <div class="label">总结余</div>
        <div class="value">{{ totalBalance >= 0 ? '' : '-' }}{{ Math.abs(totalBalance).toLocaleString('en-US', {
          minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</div>
      </div>
    </div>

    <!-- 图表容器 -->
    <div class="chart-container">
      <a-spin :spinning="loading" tip="加载中...">
        <div ref="chartContainer" style="height: 600px"></div>
      </a-spin>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import { API } from '@/config/api'
import moment from 'moment'

export default {
  name: 'TransactionReport',
  props: {
    platform: {
      type: String,
      required: true
    },
    userId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      chart: null,
      loading: false,
      chartData: {
        months: [],
        incomes: [],
        expenses: [],
        balance: []
      },
      summary: {
        total_income: 0,
        total_expense: 0,
        total_balance: 0
      },
      // 本地时间筛选状态
      localDateRange: null,
      localStartTime: null,
      localEndTime: null,
      // 防抖定时器
      debounceTimer: null
    }
  },
  computed: {
    totalIncome() {
      return this.summary.total_income
    },
    totalExpense() {
      return this.summary.total_expense
    },
    totalBalance() {
      return this.summary.total_balance
    },
    formattedMonths() {
      return this.chartData.months.map(month => {
        const [, m] = month.split('-')
        return `${parseInt(m)}月`
      })
    },
    // 时间范围预设选项
    dateRanges() {
      return {
        '今天': [moment().startOf('day'), moment().endOf('day')],
        '本周': [moment().startOf('week'), moment().endOf('week')],
        '本月': [moment().startOf('month'), moment().endOf('month')],
        '最近7天': [moment().subtract(7, 'days'), moment()],
        '最近30天': [moment().subtract(30, 'days'), moment()],
        '最近三个月': [moment().subtract(3, 'month'), moment()],
        '最近半年': [moment().subtract(6, 'month'), moment()],
        '最近一年': [moment().subtract(1, 'year'), moment()]
      }
    }
  },

  methods: {
    initChart() {
      if (this.chart) {
        this.chart.dispose()
      }
      this.chart = echarts.init(this.$refs.chartContainer)

      const option = {
        title: {
          text: '月度收支统计',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            let result = `${params[0].name}<br/>`
            params.forEach(param => {
              const value = Math.abs(param.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).toLocaleString()
              result += `${param.seriesName}：${value}元<br/>`
            })
            return result
          }
        },
        legend: {
          data: ['收入', '支出', '结余'],
          top: 30
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.formattedMonths,
          axisLabel: {
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => {
              return value.toFixed(2)
            }
          }
        },
        series: [
          {
            name: '收入',
            type: 'bar',
            data: this.chartData.incomes,
            itemStyle: {
              color: '#52c41a'
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params) => {
                return params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }
            }
          },
          {
            name: '支出',
            type: 'bar',
            data: this.chartData.expenses,
            itemStyle: {
              color: '#f5222d'
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params) => {
                return params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }
            }
          },
          {
            name: '结余',
            type: 'bar',
            data: this.chartData.balance,
            itemStyle: {
              // 根据结余正负值显示不同颜色
              color: (params) => {
                return params.value >= 0 ? '#1890ff' : '#ff4d4f'
              }
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params) => {
                const prefix = params.value >= 0 ? '' : '-'
                return prefix + Math.abs(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }
            }
          }
        ]
      }
      this.chart.setOption(option)
    },

    handleResize() {
      this.chart?.resize()
    },

    async fetchData() {
      this.loading = true
      try {
        // 构建API请求参数
        const params = {
          platform: this.platform,
          user_id: this.userId
        }

        // 如果设置了本地时间筛选，添加到请求中
        if (this.localStartTime && this.localEndTime) {
          const startTimeStamp = Math.floor(this.localStartTime.valueOf() / 1000)
          const endTimeStamp = Math.floor(this.localEndTime.valueOf() / 1000)
          params.start_time = startTimeStamp
          params.end_time = endTimeStamp
        }

        console.log('数据报表API请求参数:', params)
        const res = await request.post(API.TRANSFER_RECORD_CHART, params)

        if (res.data) {
          const { chart_data, summary } = res.data
          this.chartData = {
            months: chart_data.months || [],
            incomes: chart_data.incomes || [],
            expenses: chart_data.expenses || [],
            balance: chart_data.balance || []
          }
          this.summary = {
            total_income: summary.total_income || 0,
            total_expense: summary.total_expense || 0,
            total_balance: summary.total_balance || 0
          }

          // 更新图表
          this.$nextTick(() => {
            this.initChart()
          })
        }
      } catch (error) {
        this.$message.error('获取数据失败：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 处理时间范围选择变化
    handleDateRangeChange(dates) {
      this.localDateRange = dates

      // 清除之前的防抖定时器
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      // 如果选择了有效的时间范围，直接应用筛选
      if (dates && dates.length === 2) {
        this.localStartTime = dates[0]
        this.localEndTime = dates[1]

        // 防抖处理，避免频繁API调用
        this.debounceTimer = setTimeout(() => {
          this.fetchData()
        }, 300)
      } else if (dates === null || dates.length === 0) {
        // 如果清空了时间选择，重置为显示全部数据
        this.localStartTime = null
        this.localEndTime = null

        this.debounceTimer = setTimeout(() => {
          this.fetchData()
        }, 300)
      }
    },



    // 重置时间筛选
    handleResetTimeFilter() {
      this.localDateRange = null
      this.localStartTime = null
      this.localEndTime = null

      // 清除防抖定时器
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      // 重新获取全部数据
      this.fetchData()
    },

    // 格式化时间范围显示
    formatDateRange(startTime, endTime) {
      if (!startTime || !endTime) return ''
      return `${startTime.format('YYYY-MM-DD')} 至 ${endTime.format('YYYY-MM-DD')}`
    },
  },
  async mounted() {
    await this.fetchData()
    this.initChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    // 清理防抖定时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }
}
</script>

<style scoped>
.transaction-report {
  width: 100%;
  height: 100%;
  padding: 20px;
}

/* 时间筛选器样式 */
.time-filter-section {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
}

.filter-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.current-filter {
}

.chart-container {
  position: relative;
}

.summary-cards {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 20px;
}

.summary-card {
  padding: 5px;
  border-radius: 8px;
  text-align: center;
  min-width: 160px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.summary-card .label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
}

.summary-card .value {
  font-size: 24px;
  font-weight: bold;
}

.summary-card.income {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
}

.summary-card.income .value {
  color: #52c41a;
}

.summary-card.expense {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
}

.summary-card.expense .value {
  color: #f5222d;
}

.summary-card.balance {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
}

.summary-card.balance .value {
  color: #1890ff;
}

.summary-card.balance.negative {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
}

.summary-card.balance.negative .value {
  color: #f5222d;
}

.ant-modal-body{
  padding:0 !important;
}

</style>